import { useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { auditCompleteCase } from "../../../lib/fetchApi";
import { notificationActions } from "../../../store/notifications";
import { filterActions } from "../../../store/filter";
import { useUserTabEditPermissionDisabled } from "../../../lib/usersPermissions";
import Button from "../../common/Button";
import Backdrop from "../../common/modal/Backdrop";
import Modal from "../../common/Modal";
import ModalInnerWrapper from "../../common/ModalInnerWrapper";
import PannelBody from "../../fragments/PannelBody";
import CheckboxCell from "../../common/interactive/CheckboxCell";
import classesCell from "./BodyCell.module.css";

function flattenErrorValues(value) {
    if (value == null) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.flatMap(flattenErrorValues);
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed ? [trimmed] : [];
    }
    if (typeof value === "object") {
        return Object.values(value).flatMap(flattenErrorValues);
    }
    return [`${value}`.trim()].filter(Boolean);
}

function collectHardBlockMessages(response) {
    if (!response || typeof response !== "object") {
        return [];
    }
    const source =
        response.data != null && typeof response.data === "object" ? response.data : response;
    const candidates = [
        source.force_errors,
        source.forceErrors,
        source.validation_errors,
        source.validationErrors,
        source.errors,
        source.errorMessage,
        response.errorMessage,
    ];
    return [...new Set(candidates.flatMap(flattenErrorValues).filter(Boolean))];
}

function typeLabel(type) {
    switch (type) {
        case "original":
        case "lien":
            return "Original";
        case "amend":
        case "amended":
            return "Amended";
        default:
            return "—";
    }
}

function outcomeDisplay(outcome) {
    if (outcome === "success") {
        return "Success";
    }
    if (outcome === "error" || outcome === "do_not_lien") {
        return "Error";
    }
    // Legacy outcomes from older API responses
    switch (outcome) {
        case "created_original":
        case "created_amended":
            return "Success";
        case "validation_failed":
        case "create_failed":
        case "gsccca_error":
            return "Error";
        default:
            return outcome || "—";
    }
}

function normalizePracticeResult(row) {
    if (!row || typeof row !== "object") {
        return null;
    }
    if (row.outcome === "skipped") {
        return null;
    }

    let type = row.type ?? null;
    if (!type && row.created_type) {
        type = row.created_type === "amend" ? "amend" : "original";
    }
    if (!type && (row.outcome === "created_amended" || row.outcome === "created_original")) {
        type = row.outcome === "created_amended" ? "amend" : "original";
    }

    let message = typeof row.message === "string" ? row.message.trim() : "";
    if (!message && Array.isArray(row.messages)) {
        message = row.messages.map((m) => `${m}`.trim()).filter(Boolean).join("; ");
    }

    return {
        case_practice_id: row.case_practice_id ?? null,
        practice_name: row.practice_name ?? null,
        type,
        outcome: row.outcome,
        message,
    };
}

/**
 * WorkQ case-level Audit Complete checkbox.
 * Confirm processes all practices; always saves Audit Complete (unless already set).
 * Overview modal shows a Practice / Type / Outcome / Message table (eligible only).
 */
export default function AuditCompleteCell({ caseData }) {
    const dispatch = useDispatch();
    const userToken = useSelector((state) => state.user.token);
    const permissionDisabled = useUserTabEditPermissionDisabled("lien_edit");
    const caseId = caseData?.case?.id;
    const isChecked = Boolean(
        caseData?.case?.audit_complete ?? caseData?.case?.auditComplete,
    );
    const canMarkAuditComplete = !isChecked && !permissionDisabled && Boolean(caseId);

    const [showConfirm, setShowConfirm] = useState(false);
    const [showHardErrors, setShowHardErrors] = useState(null);
    const [showOverview, setShowOverview] = useState(null);
    const [loading, setLoading] = useState(false);

    function openConfirm() {
        if (!canMarkAuditComplete || loading) {
            return;
        }
        setShowConfirm(true);
    }

    function closeConfirm() {
        if (!loading) {
            setShowConfirm(false);
        }
    }

    function closeHardErrors() {
        setShowHardErrors(null);
    }

    function closeOverview() {
        setShowOverview(null);
    }

    function applyAuditCompleteToWorkQ(result) {
        const casePayload = result?.case_data?.case;
        const fields = [{ field: "audit_complete", value: 1 }];
        if (casePayload?.audit_complete_at != null) {
            fields.push({ field: "audit_complete_at", value: casePayload.audit_complete_at });
        }
        if (casePayload?.audit_complete_by_id != null) {
            fields.push({ field: "audit_complete_by_id", value: casePayload.audit_complete_by_id });
        }

        dispatch(
            filterActions.updateItemInWorkQRow({
                case_id: caseId,
                folder: "case",
                item: {
                    itemId: caseId,
                    fields,
                },
            }),
        );
    }

    async function confirmHandler() {
        setLoading(true);
        try {
            const result = await auditCompleteCase(userToken, caseId, dispatch);
            const auditSaved = Boolean(result?.case_data?.case?.audit_complete);

            if (!auditSaved) {
                setShowConfirm(false);
                const combined = collectHardBlockMessages(result);
                setShowHardErrors(
                    combined.length > 0
                        ? combined
                        : ["Audit Complete could not be saved."],
                );
                return;
            }

            applyAuditCompleteToWorkQ(result);
            setShowConfirm(false);

            const successMessage =
                result?.message || result?.data?.message || "Audit Complete saved.";
            dispatch(
                notificationActions.setError({
                    errorMessage: successMessage,
                    type: "success",
                }),
            );

            const rawResults =
                result?.practice_results ?? result?.data?.practice_results;
            const practiceResults = (Array.isArray(rawResults) ? rawResults : [])
                .map(normalizePracticeResult)
                .filter(Boolean);
            setShowOverview(practiceResults);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <div
                className={classesCell.body_cell}
                onClick={(e) => {
                    e.stopPropagation();
                    openConfirm();
                }}
                style={canMarkAuditComplete ? { cursor: "pointer" } : undefined}
                title={
                    isChecked
                        ? "Audit Complete (cannot uncheck until patient returns to treatment)"
                        : permissionDisabled
                          ? "You do not have permission to mark Audit Complete"
                          : "Mark Audit Complete for this case"
                }
            >
                <CheckboxCell
                    checked={isChecked}
                    isDisabled={true}
                    allowPointerWhenDisabled={canMarkAuditComplete}
                    identifier={{
                        type: "lien_edit",
                        permissionType: "lien_edit",
                        field: "audit_complete",
                        type_id: caseId,
                        folder: "case",
                    }}
                />
            </div>

            {showConfirm && (
                <>
                    {ReactDOM.createPortal(
                        <Backdrop hide={closeConfirm} />,
                        document.getElementById("backdrop-root"),
                    )}
                    {ReactDOM.createPortal(
                        <Modal>
                            <ModalInnerWrapper
                                closeModal={closeConfirm}
                                modalTitle="Confirm Audit Complete"
                            >
                                <PannelBody>
                                    <p>
                                        Mark this case as Audit Complete? ECM will review every
                                        practice and create Original or Amended Liens where
                                        eligible, skipping only practices that are still in their
                                        waiting period. Audit Complete will be saved even if some
                                        liens fail to be created. After completion, you'll see a
                                        summary showing which liens were created successfully and
                                        which failed.
                                    </p>
                                </PannelBody>
                                <PannelBody>
                                    <div className="flex-container flex-container-end">
                                        <Button click={closeConfirm} btnStyle="btn_white" isDisabled={loading}>
                                            Cancel
                                        </Button>
                                        <Button click={confirmHandler} isLoading={loading}>
                                            Confirm
                                        </Button>
                                    </div>
                                </PannelBody>
                            </ModalInnerWrapper>
                        </Modal>,
                        document.getElementById("overlay-root"),
                    )}
                </>
            )}

            {showHardErrors && (
                <>
                    {ReactDOM.createPortal(
                        <Backdrop hide={closeHardErrors} />,
                        document.getElementById("backdrop-root"),
                    )}
                    {ReactDOM.createPortal(
                        <Modal>
                            <ModalInnerWrapper
                                closeModal={closeHardErrors}
                                modalTitle="Audit Complete blocked"
                            >
                                <PannelBody>
                                    {showHardErrors.map((item) => (
                                        <p key={item}>{item}</p>
                                    ))}
                                </PannelBody>
                            </ModalInnerWrapper>
                        </Modal>,
                        document.getElementById("overlay-root"),
                    )}
                </>
            )}

            {showOverview && (
                <>
                    {ReactDOM.createPortal(
                        <Backdrop hide={closeOverview} />,
                        document.getElementById("backdrop-root"),
                    )}
                    {ReactDOM.createPortal(
                        <Modal>
                            <ModalInnerWrapper
                                closeModal={closeOverview}
                                modalTitle="Audit Complete overview"
                            >
                                <PannelBody>
                                    <p>Audit Complete is marked for this case.</p>
                                    <p>
                                        <strong>Practice results:</strong>
                                    </p>
                                    {showOverview.length === 0 ? (
                                        <p>No practices were eligible for a lien create.</p>
                                    ) : (
                                        <table
                                            className="table-border-radius-8"
                                            style={{ width: "100%", marginTop: 8 }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>Practice</th>
                                                    <th>Type</th>
                                                    <th>Outcome</th>
                                                    <th>Message</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {showOverview.map((row, index) => {
                                                    const name =
                                                        row.practice_name ||
                                                        (row.case_practice_id
                                                            ? `Practice #${row.case_practice_id}`
                                                            : "Case");
                                                    const key = `${row.case_practice_id ?? "case"}-${index}`;
                                                    return (
                                                        <tr key={key}>
                                                            <td>{name}</td>
                                                            <td>{typeLabel(row.type)}</td>
                                                            <td>{outcomeDisplay(row.outcome)}</td>
                                                            <td>{row.message || "—"}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </PannelBody>
                            </ModalInnerWrapper>
                        </Modal>,
                        document.getElementById("overlay-root"),
                    )}
                </>
            )}
        </>
    );
}
